import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal } from "lucide-react";
import { SharingBtn } from "@/components/Sharing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Copy, Clipboard, Eye, Download, Search } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getSelectSellers } from "@/Api/sellers";
import { storeDistributionsSellerToSellerWithFiles } from "@/Api/distributionsSellerToSeller";
import {
  getSellerToSellerBalancesSimplified,
  getSellerToSellerDistributionDetails,
  distributionBetweenSellersBulkWithFiles,
} from "@/Api/distributions";
import { FileUpload } from "../../components/FileUpload";
import { getShowContests } from "@/Api/contests";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { LoaderSpin, CreateLoaderInput } from "@/components/Loaders";
import { useAuth } from "@/hooks/auth";
import { Mask } from "@/utils/mask";

// Interfaces para o novo endpoint simplificado
interface SimplifiedSellerBalance {
  current_balance: string;
  held_balance: string;
}

interface SimplifiedSeller {
  id: number;
  name: string;
  contest_product_balance: SimplifiedSellerBalance;
}

interface SimplifiedProduct {
  contest_product_classification: {
    id: number;
    product_name: string;
    sale_price: number;
    quota_total_quantity: number;
    quota_total_distributed: string;
  };
  sellers: SimplifiedSeller[];
}

interface Seller {
  id: number;
  email: string;
  user: {
    name: string;
  };
}

export const TableData = ({
  dataApi,
  pageActions,
}: {
  dataApi: any;
  pageActions: {
    update: () => Promise<void>;
    next?: () => Promise<void>;
    previous?: () => Promise<void>;
    lastPage?: () => Promise<void>;
  };
}) => {
  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Loteria</TableHead>
            <TableHead>Data de validade</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((contest: any) => (
            <TableRow key={contest.id}>
              <TableCell className="truncate max-w-80">{contest.name}</TableCell>
              <TableCell>
                <div
                  className="inline-block px-2 py-1 rounded text-white text-sm"
                  style={{ backgroundColor: contest.lottery?.color || "#gray" }}
                >
                  {contest.lottery?.name}
                </div>
              </TableCell>
              <TableCell>
                {contest.validation_date
                  ? Mask.transformMaskBirthdayInBr(contest.validation_date)
                  : "Não informado"}
              </TableCell>
              <TableCell className="text-center">
                <SharingBtn
                  labels={{ title: "Distribuir entre Vendedores" }}
                  params={{
                    func: getShowContests,
                    funcParams: contest.uuid,
                  }}
                  className="sm:max-w-[700px]"
                  render={(responseApi: any) => (
                    <InsideContestModal
                      contestData={responseApi}
                      pageActions={pageActions}
                    />
                  )}
                  loader={<CreateLoaderInput lines={4} />}
                />
              </TableCell>
            </TableRow>
          ))}
          {dataApi?.data?.length < 1 && (
            <TableRow>
              <TableCell colSpan={4}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};

function InsideContestModal({
  contestData,
  pageActions,
}: {
  contestData: any;
  pageActions: {
    update: () => Promise<void>;
  };
}) {
  const { user } = useAuth();
  const [simplifiedData, setSimplifiedData] = useState<SimplifiedProduct[]>([]);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [isModalLoading, setIsModalLoading] = useState(true);
  const [distributingProductId, setDistributingProductId] = useState<
    number | null
  >(null);
  const [quotaStates, setQuotaStates] = useState<{
    [key: string]: { sellerId: number; quota: number }[];
  }>({});
  // Ref para armazenar as alterações do usuário (não é resetado pelo fetchPage)
  const userChangesRef = useRef<{
    [productId: string]: { sellerId: number; quota: number }[];
  }>({});
  const [confirmDialogProduct, setConfirmDialogProduct] = useState<any>(null);
  const [confirmDialogFiles, setConfirmDialogFiles] = useState<File[]>([]);
  const [showDistributeAllDialog, setShowDistributeAllDialog] = useState(false);
  // Snapshot dos dados a distribuir quando o dialog é aberto
  const [distributeAllSnapshot, setDistributeAllSnapshot] = useState<{
    [productId: string]: { sellerId: number; quota: number }[];
  } | null>(null);
  const [distributeAllFiles, setDistributeAllFiles] = useState<File[]>([]);
  const [copiedDistribution, setCopiedDistribution] = useState<{
    sellerId: number;
    quota: number;
    sellerName: string;
  } | null>(null);
  const [loadingButton, setLoadingButton] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProductDetails, setSelectedProductDetails] =
    useState<any>(null);
  const [productDetails, setProductDetails] = useState<any[]>([]);
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<{
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
    from: number;
    to: number;
  } | null>(null);
  // Filtro por produto
  const [productFilter, setProductFilter] = useState("");

  const sellerId = user?.seller?.id;
  const unityId = user?.unity?.[0]?.id;

  // Buscar vendedores primeiro
  useEffect(() => {
    const fetchSellers = async () => {
      if (!unityId) return;

      try {
        const { data } = await getSelectSellers({
          unity_id: undefined,
        });
        // Filtrar o vendedor atual
        const filteredSellers = (data?.response || []).filter(
          (seller: Seller) => seller.id !== sellerId
        );
        setSellers(filteredSellers);
      } catch (error) {
        console.error("Erro ao buscar vendedores:", error);
      }
    };

    fetchSellers();
  }, [unityId, sellerId]);

  const fetchPage = async (page: number, filter?: string) => {
    if (sellers.length === 0) return;

    setIsModalLoading(true);
    try {
      const searchFilter = filter !== undefined ? filter : productFilter;
      const productName = searchFilter ? `B${searchFilter}` : undefined;
      const response = await getSellerToSellerBalancesSimplified(
        String(contestData.id),
        page,
        100,
        productName
      );

      if (response.data?.status) {
        const responseData = response.data.response;

        // Verificar se a resposta é paginada (tem .data) ou não paginada (array direto)
        const isPaginated = responseData && typeof responseData === 'object' && 'data' in responseData;
        const data = isPaginated
          ? (responseData.data as SimplifiedProduct[])
          : (Array.isArray(responseData) ? responseData as SimplifiedProduct[] : []);

        setSimplifiedData(data);

        if (isPaginated) {
          setPaginationInfo({
            current_page: responseData.current_page,
            per_page: responseData.per_page,
            total: responseData.total,
            last_page: responseData.last_page,
            from: responseData.from,
            to: responseData.to,
          });
        } else {
          setPaginationInfo({
            current_page: 1,
            per_page: data.length,
            total: data.length,
            last_page: 1,
            from: 1,
            to: data.length,
          });
        }

        // Acumular quotaStates - manter alterações de outras páginas
        setQuotaStates((prev) => {
          const newStates = { ...prev };
          data.forEach((product) => {
            const productId = product.contest_product_classification.id;
            if (!newStates[productId]) {
              newStates[productId] = sellers.map((seller) => ({
                sellerId: seller.id,
                quota: 0,
              }));
            }
          });
          return newStates;
        });
      }
    } catch (error) {
      console.error("Erro ao buscar dados do concurso:", error);
      toast.error("Erro ao carregar distribuições");
    } finally {
      setIsModalLoading(false);
    }
  };

  // Buscar dados quando os vendedores estiverem carregados
  useEffect(() => {
    if (sellers.length > 0) {
      fetchPage(currentPage);
    }
  }, [contestData.id, sellers]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (paginationInfo?.last_page || 1)) {
      setCurrentPage(page);
      fetchPage(page);
    }
  };

  const handleFilterChange = (value: string) => {
    const numericValue = value.replace(/\D/g, "");
    setProductFilter(numericValue);
  };

  const handleFilterSearch = () => {
    setCurrentPage(1);
    fetchPage(1, productFilter);
  };

  const handleFilterClear = () => {
    setProductFilter("");
    setCurrentPage(1);
    fetchPage(1, "");
  };

  function getCurrentSellerBalance(productId: number): number {
    const product = simplifiedData.find(
      (p) => p.contest_product_classification.id === productId
    );

    // Agora pegar a quantidade disponível diretamente da classificação do produto
    const totalQuantity = parseInt(
      String(
        product?.contest_product_classification.quota_total_quantity || "0"
      )
    );
    const distributedQuantity = parseInt(
      String(
        product?.contest_product_classification.quota_total_distributed || "0"
      )
    );

    // Saldo disponível é o distribuído menos o total (conforme explicado pelo usuário)
    // Nunca negativo: inconsistência de dados não deve travar/confundir a distribuição
    return Math.max(0, totalQuantity - distributedQuantity);
  }

  function getAvailableBalance(productId: number): number {
    return getCurrentSellerBalance(productId);
  }

  function handleQuotaChange(
    productId: number,
    targetSellerId: number,
    quota: number
  ) {
    setQuotaStates((prev) => {
      const newStates = {
        ...prev,
        [productId]: prev[productId].map((item) =>
          item.sellerId === targetSellerId ? { ...item, quota } : item
        ),
      };
      // Salvar no ref para persistir entre páginas
      userChangesRef.current[productId] = newStates[productId];
      return newStates;
    });
  }

  function handleCopyIndividualDistribution(
    targetSellerId: number,
    quota: number,
    sellerName: string
  ) {
    setCopiedDistribution({
      sellerId: targetSellerId,
      quota,
      sellerName,
    });
    toast.success(`Distribuição de ${sellerName} (${quota} cotas) copiada!`);
  }

  function handlePasteDistribution(productId: number, targetSellerId: number) {
    if (!copiedDistribution) {
      toast.error("Nenhuma distribuição copiada encontrada.");
      return;
    }

    setQuotaStates((prev) => {
      const newStates = {
        ...prev,
        [productId]: prev[productId].map((item) =>
          item.sellerId === targetSellerId
            ? { ...item, quota: copiedDistribution.quota }
            : item
        ),
      };
      // Salvar no ref para persistir entre páginas
      userChangesRef.current[productId] = newStates[productId];
      return newStates;
    });

    const targetSeller = sellers.find((s) => s.id === targetSellerId);
    toast.success(
      `${copiedDistribution.quota} cotas coladas para ${targetSeller?.user?.name}!`
    );
  }

  function getQuotaValidationInfo(
    productId: number,
    currentInputs: { sellerId: number; quota: number }[]
  ) {
    const totalInputQuotas = currentInputs.reduce(
      (sum, item) => sum + item.quota,
      0
    );

    const availableBalance = getAvailableBalance(productId);
    const isOverLimit = totalInputQuotas > availableBalance;
    const remaining = availableBalance - totalInputQuotas;

    return {
      totalInputQuotas,
      availableBalance,
      isOverLimit,
      remaining,
      canProceed: !isOverLimit,
    };
  }

  async function handleSubmitDistribution(product: any, files: File[]) {
    if (files.length === 0) {
      toast.error("É obrigatório anexar um arquivo para distribuir.");
      return;
    }

    setDistributingProductId(product.id);
    const localQuotas = quotaStates[product.id] || [];

    // Filtrar apenas vendedores com cotas > 0
    const modifiedSellers = localQuotas.filter((currentQuota) => {
      return currentQuota.quota > 0;
    });

    if (modifiedSellers.length === 0) {
      toast.warning("Nenhuma alteração foi detectada para enviar.");
      setDistributingProductId(null);
      return;
    }

    const validationInfo = getQuotaValidationInfo(product.id, localQuotas);

    if (validationInfo.isOverLimit) {
      const excess =
        validationInfo.totalInputQuotas - validationInfo.availableBalance;
      toast.error(
        `Não é possível prosseguir. Você está tentando distribuir ${validationInfo.totalInputQuotas} cotas, mas há apenas ${validationInfo.availableBalance} disponíveis. Excesso: ${excess} cotas.`
      );
      setDistributingProductId(null);
      return;
    }

    const formData = new FormData();
    formData.append("contest_id", String(contestData.id));
    formData.append("lottery_id", String(product.lottery_id || contestData.lottery_id));
    formData.append("from_seller_id", String(sellerId));
    formData.append("unity_id", String(unityId));
    formData.append("contest_product_classification_id", String(product.id));

    modifiedSellers.forEach((sq, index) => {
      formData.append(`sellers[${index}][id]`, String(sq.sellerId));
      formData.append(`sellers[${index}][quota_quantity]`, String(sq.quota));
    });

    files.forEach((file) => {
      formData.append("files[]", file);
    });

    try {
      const { ok } = await storeDistributionsSellerToSellerWithFiles(formData);
      if (!ok) {
        throw new Error("Erro ao registrar distribuição");
      }

      toast.success(
        `Distribuição salva com sucesso (${modifiedSellers.length} vendedor${
          modifiedSellers.length > 1 ? "es" : ""
        } atualizado${modifiedSellers.length > 1 ? "s" : ""})`
      );

      // Limpar states e recarregar dados
      setQuotaStates({});
      userChangesRef.current = {};
      await fetchPage(currentPage);
      await pageActions.update();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Erro ao criar distribuição"
      );
    } finally {
      setDistributingProductId(null);
    }
  }

  async function handleShowDetails(productId: number, productName: string) {
    setSelectedProductDetails({ id: productId, name: productName });
    const { ok, data } = await getSellerToSellerDistributionDetails(
      String(contestData.id),
      productId
    );

    if (ok) {
      setProductDetails(data.response?.distributions || []);
      setShowDetailsModal(true);
    }
  }

  // Captura snapshot de todas as alterações quando abre o dialog
  const handleOpenDistributeAllDialog = () => {
    // Combinar dados do ref (alterações de todas as páginas) com quotaStates atual
    const snapshot: { [productId: string]: { sellerId: number; quota: number }[] } = {};

    // 1. Primeiro, pega todas as alterações do ref (páginas anteriores)
    Object.entries(userChangesRef.current).forEach(([productId, quotas]) => {
      const quotasWithValues = quotas.filter(q => q.quota > 0);
      if (quotasWithValues.length > 0) {
        snapshot[productId] = quotasWithValues.map(q => ({ ...q }));
      }
    });

    // 2. Depois, pega do quotaStates atual (página atual) - sobrescreve se já existir
    Object.entries(quotaStates).forEach(([productId, quotas]) => {
      const quotasWithValues = quotas.filter(q => q.quota > 0);
      if (quotasWithValues.length > 0) {
        snapshot[productId] = quotasWithValues.map(q => ({ ...q }));
      }
    });

    setDistributeAllSnapshot(snapshot);
    setShowDistributeAllDialog(true);
  };

  async function handleDistributeAll() {
    if (distributeAllFiles.length === 0) {
      toast.error("É obrigatório anexar um arquivo para distribuir.");
      return;
    }

    if (!distributeAllSnapshot || Object.keys(distributeAllSnapshot).length === 0) {
      toast.error("Nenhum produto para distribuir.");
      return;
    }

    setLoadingButton(true);
    setShowDistributeAllDialog(false);

    const distributions: Array<{
      contest_product_classification_id: number;
      from_seller_id: number;
      sellers: Array<{ id: number; quota_quantity: number }>;
    }> = [];
    let skippedCount = 0;
    let validationErrorCount = 0;

    // Usar o snapshot capturado quando o dialog foi aberto
    const allProductIds = Object.keys(distributeAllSnapshot);

    for (const productIdStr of allProductIds) {
      const productId = parseInt(productIdStr);
      const sellersToDistribute = distributeAllSnapshot[productId] || [];

      if (sellersToDistribute.length === 0) {
        skippedCount++;
        continue;
      }

      // Só validar se o produto está na página atual
      const productInCurrentPage = simplifiedData.find(
        (p) => p.contest_product_classification.id === productId
      );
      if (productInCurrentPage) {
        const validationInfo = getQuotaValidationInfo(productId, sellersToDistribute);
        if (validationInfo.isOverLimit) {
          validationErrorCount++;
          continue;
        }
      }

      distributions.push({
        contest_product_classification_id: productId,
        from_seller_id: sellerId,
        sellers: sellersToDistribute.map((sq) => ({
          id: sq.sellerId,
          quota_quantity: sq.quota,
        })),
      });
    }

    if (distributions.length === 0) {
      setLoadingButton(false);
      toast.info("Nenhuma distribuição válida para salvar.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("contest_id", String(contestData.id));
      formData.append("unity_id", String(unityId));
      formData.append("distributions", JSON.stringify(distributions));
      distributeAllFiles.forEach((file) => {
        formData.append("files[]", file);
      });

      const { ok, data } = await distributionBetweenSellersBulkWithFiles(formData);

      if (ok) {
        // Limpar todos os states após distribuir
        setQuotaStates({});
        setDistributeAllSnapshot(null);
        userChangesRef.current = {};

        // Recarregar dados simplificados após salvar
        await fetchPage(currentPage);

        const message =
          skippedCount > 0 || validationErrorCount > 0
            ? `${distributions.length} distribuições salvas com sucesso!${skippedCount > 0 ? ` ${skippedCount} produtos sem cotas ignorados.` : ""}${validationErrorCount > 0 ? ` ${validationErrorCount} produtos com erro de validação.` : ""}`
            : `Todas as ${distributions.length} distribuições foram salvas com sucesso!`;
        toast.success(message);
      } else {
        toast.error(data?.message || "Erro ao salvar distribuições.");
      }
    } catch (error) {
      console.error("Erro ao salvar distribuições:", error);
      toast.error("Erro ao salvar distribuições.");
    } finally {
      setLoadingButton(false);
      setDistributeAllFiles([]);
      setDistributeAllSnapshot(null);
    }

    await pageActions.update();
  }

  if (isModalLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        <LoaderSpin />
      </div>
    );
  }

  return (
    <>
      <div className="h-[70vh] overflow-hidden flex flex-col" style={{ maxHeight: "70vh" }}>
        <div className="w-full h-auto flex-shrink-0">
          {/* Header melhorado do concurso */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-lg p-4 mb-4 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div
                  className="px-3 py-1.5 rounded-lg text-white text-sm font-semibold shadow-sm"
                  style={{ backgroundColor: contestData?.lottery?.color || '#023859' }}
                >
                  {contestData?.lottery?.name}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    {contestData?.name}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Validade: {contestData?.validation_date
                      ? Mask.transformMaskBirthdayInBr(contestData?.validation_date)
                      : "Não informado"}
                  </p>
                </div>
              </div>
              <Button
                onClick={handleOpenDistributeAllDialog}
                className={cn("flex gap-2", colors.bg_primary)}
                disabled={loadingButton || isModalLoading}
              >
                {loadingButton ? (
                  <>
                    Distribuindo... <LoaderSpin />
                  </>
                ) : (
                  "Distribuir Tudo"
                )}
              </Button>
            </div>
          </div>

          {/* Filtro por produto */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">B:</span>
              <Input
                type="text"
                placeholder="Ex: 200"
                value={productFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleFilterSearch()}
                className="w-24"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleFilterSearch}
              className="flex items-center gap-1"
            >
              <Search className="w-4 h-4" />
              Buscar
            </Button>
            {productFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFilterClear}
              >
                Limpar
              </Button>
            )}
            {paginationInfo && (
              <span className="text-sm text-slate-500 dark:text-slate-400 ml-auto">
                Exibindo {paginationInfo.from}-{paginationInfo.to} de {paginationInfo.total} produtos
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-3">
            <Table>
              <TableHeader className="sticky top-0 bg-white dark:bg-slate-900 z-10">
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead>Saldo Disponível</TableHead>
                  <TableHead className="text-center">Distribuição</TableHead>
                  <TableHead className="text-center">Detalhes</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {simplifiedData?.map((productData) => {
                  const product = {
                    id: productData.contest_product_classification.id,
                    product_name: productData.contest_product_classification.product_name,
                    lottery_id: contestData.lottery?.id,
                  };
                  const localQuotas = quotaStates[product.id] || [];
                  const availableBalance = getAvailableBalance(product.id);

                  return (
                    <TableRow key={product.id}>
                      <TableCell className="align-top">
                        {product.product_name}
                      </TableCell>
                      <TableCell className="text-center align-top">
                        <div className="flex flex-col items-center gap-1">
                          <span className="font-medium text-lg">
                            {availableBalance}
                          </span>
                          <span className="text-xs text-gray-500">
                            cotas disponíveis
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="border rounded p-2 mb-2 max-h-40 overflow-auto">
                          {sellers.map((seller) => {
                            const quotaValue =
                              localQuotas.find((q) => q.sellerId === seller.id)
                                ?.quota ?? 0;

                            return (
                              <div
                                key={seller.id}
                                className="flex items-center gap-2 mb-2 border-b border-gray-200 dark:border-gray-700 pb-2"
                              >
                                <div className="flex-1 text-sm">
                                  <span className="font-medium">
                                    {seller.user?.name}
                                  </span>
                                </div>
                                <Input
                                  type="number"
                                  min="0"
                                  max={availableBalance}
                                  value={quotaValue}
                                  onChange={(e) =>
                                    handleQuotaChange(
                                      product.id,
                                      seller.id,
                                      parseInt(e.target.value) || 0
                                    )
                                  }
                                  className="w-20"
                                  disabled={
                                    distributingProductId === product.id
                                  }
                                />
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() =>
                                      handleCopyIndividualDistribution(
                                        seller.id,
                                        quotaValue,
                                        seller.user?.name
                                      )
                                    }
                                    disabled={quotaValue === 0}
                                    title="Copiar distribuição"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() =>
                                      handlePasteDistribution(
                                        product.id,
                                        seller.id
                                      )
                                    }
                                    disabled={!copiedDistribution}
                                    title="Colar distribuição"
                                  >
                                    <Clipboard className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Validação e resumo */}
                        {(() => {
                          const validationInfo = getQuotaValidationInfo(
                            product.id,
                            localQuotas
                          );
                          if (validationInfo.totalInputQuotas === 0)
                            return null;

                          return (
                            <div className="mt-2 mb-2 p-2 rounded bg-gray-50 dark:bg-gray-800">
                              <div className="text-sm">
                                <span className="font-medium">
                                  Total: {validationInfo.totalInputQuotas} /{" "}
                                  {validationInfo.availableBalance}
                                </span>
                                {validationInfo.isOverLimit ? (
                                  <div className="text-red-600 dark:text-red-400 text-xs mt-1">
                                    ⚠️ Excesso de{" "}
                                    {validationInfo.totalInputQuotas -
                                      validationInfo.availableBalance}{" "}
                                    cotas sobre o limite disponível
                                  </div>
                                ) : validationInfo.remaining > 0 ? (
                                  <div className="text-orange-600 dark:text-orange-400 text-xs mt-1">
                                    💡 Ainda restam {validationInfo.remaining}{" "}
                                    cotas disponíveis
                                  </div>
                                ) : (
                                  <div className="text-green-600 dark:text-green-400 text-xs mt-1">
                                    ✅ Utilizando todo o saldo disponível
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        <Button
                          variant="outline"
                          className={cn(
                            "w-full",
                            (() => {
                              const validationInfo = getQuotaValidationInfo(
                                product.id,
                                localQuotas
                              );
                              return validationInfo.isOverLimit
                                ? "bg-red-100 border-red-300 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:border-red-700 dark:text-red-300"
                                : colors.bg_primary;
                            })()
                          )}
                          disabled={
                            distributingProductId === product.id ||
                            (() => {
                              const validationInfo = getQuotaValidationInfo(
                                product.id,
                                localQuotas
                              );
                              const hasValidChanges = localQuotas.some(
                                (quota) => quota.quota > 0
                              );
                              return (
                                validationInfo.isOverLimit || !hasValidChanges
                              );
                            })()
                          }
                          onClick={() => setConfirmDialogProduct(product)}
                        >
                          {distributingProductId === product.id ? (
                            <LoaderSpin />
                          ) : (
                            (() => {
                              const validationInfo = getQuotaValidationInfo(
                                product.id,
                                localQuotas
                              );
                              const hasValidChanges = localQuotas.some(
                                (quota) => quota.quota > 0
                              );

                              if (validationInfo.isOverLimit) {
                                return "Distribuição bloqueada (excede limite)";
                              }
                              if (!hasValidChanges) {
                                return "Defina cotas para distribuir";
                              }
                              return "Fazer distribuição";
                            })()
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="text-center align-top">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() =>
                            handleShowDetails(
                              product.id,
                              product.product_name
                            )
                          }
                          className="h-8 w-8"
                          title="Ver detalhes da distribuição"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {simplifiedData?.length < 1 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      Nenhum produto encontrado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Paginação */}
        {paginationInfo && paginationInfo.last_page > 1 && (
          <div className="flex justify-between items-center mt-4 pt-4 border-t flex-shrink-0">
            <span className="text-sm text-slate-500 dark:text-slate-400">
              Página {paginationInfo.current_page} de {paginationInfo.last_page}
            </span>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    className={cn("h-8", colors.bg_primary)}
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(currentPage - 1);
                    }}
                  />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink
                    href="#"
                    className={cn(
                      "size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent"
                    )}
                  >
                    <span className="pointer-events-none dark:text-[#F4F4F5]">
                      {paginationInfo.current_page}
                    </span>
                  </PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink
                    href="#"
                    className="text-black h-8 hover:bg-stone-950/10"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(paginationInfo.last_page);
                    }}
                  >
                    <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                  </PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    className={cn("h-8", colors.bg_primary)}
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(currentPage + 1);
                    }}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>

      {/* Dialog de confirmação individual */}
      <Dialog
        open={!!confirmDialogProduct}
        onOpenChange={(open) => !open && setConfirmDialogProduct(null)}
      >
        <DialogContent className="z-[60]" style={{ zIndex: 60 }}>
          <DialogHeader>
            <DialogTitle>Confirmar Distribuição</DialogTitle>
            <DialogDescription>
              {confirmDialogProduct &&
                (() => {
                  const confirmLocalQuotas =
                    quotaStates[confirmDialogProduct.id] || [];
                  const confirmTotalQuotas = confirmLocalQuotas.reduce(
                    (sum, item) => sum + item.quota,
                    0
                  );
                  return (
                    <div className="space-y-2">
                      <p>
                        Você está prestes a distribuir as cotas para o produto:
                      </p>
                      <p className="font-semibold">
                        {confirmDialogProduct.product_name}
                      </p>
                      <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded">
                        <p className="text-sm mb-2">
                          <strong>Distribuição por vendedor:</strong>
                        </p>
                        <div className="space-y-1 mb-3">
                          {confirmLocalQuotas.map((quota) => {
                            const seller = sellers.find(
                              (s) => s.id === quota.sellerId
                            );
                            if (quota.quota > 0) {
                              return (
                                <div key={quota.sellerId} className="text-sm">
                                  <span className="font-medium">
                                    {seller?.user?.name}
                                  </span>{" "}
                                  -{" "}
                                  <span className="text-blue-600 dark:text-blue-400">
                                    {quota.quota} cotas
                                  </span>
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                        <div className="border-t pt-2">
                          <p className="text-sm">
                            <strong>Total de cotas a distribuir:</strong>{" "}
                            {confirmTotalQuotas}
                          </p>
                          <p className="text-sm">
                            <strong>Máximo disponível:</strong>{" "}
                            {getAvailableBalance(confirmDialogProduct.id)}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Esta ação não pode ser desfeita. Deseja continuar?
                      </p>
                    </div>
                  );
                })()}
            </DialogDescription>
          </DialogHeader>
          <FileUpload
            files={confirmDialogFiles}
            onFilesChange={setConfirmDialogFiles}
            inputId="file-upload-confirm-seller-seller"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDialogProduct(null);
                setConfirmDialogFiles([]);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (confirmDialogProduct) {
                  handleSubmitDistribution(confirmDialogProduct, confirmDialogFiles);
                  setConfirmDialogProduct(null);
                  setConfirmDialogFiles([]);
                }
              }}
              className={cn(colors.bg_primary)}
              disabled={confirmDialogFiles.length === 0}
            >
              Confirmar Distribuição
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de distribuir tudo */}
      <Dialog
        open={showDistributeAllDialog}
        onOpenChange={setShowDistributeAllDialog}
      >
        <DialogContent className="z-[60] max-w-2xl" style={{ zIndex: 60 }}>
          <DialogHeader>
            <DialogTitle>Confirmar Distribuição Geral</DialogTitle>
            <DialogDescription>
              {(() => {
                if (!distributeAllSnapshot) {
                  return (
                    <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900 rounded">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        Nenhum produto tem cotas definidas para distribuição.
                      </p>
                    </div>
                  );
                }

                const productIds = Object.keys(distributeAllSnapshot);
                let grandTotal = 0;

                return (
                  <div className="space-y-3">
                    <p>
                      Você está prestes a distribuir as cotas para{" "}
                      <strong>todos os produtos</strong> do concurso:
                    </p>
                    <p className="font-semibold">
                      {contestData?.name} - {contestData?.lottery?.name}
                    </p>

                    {productIds.length > 0 ? (
                      <>
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded max-h-64 overflow-y-auto">
                          <p className="text-sm font-medium mb-3">
                            Resumo da distribuição ({productIds.length} produto{productIds.length > 1 ? "s" : ""}):
                          </p>
                          <div className="space-y-4">
                            {productIds.map((productIdStr) => {
                              const productId = parseInt(productIdStr);
                              const quotasWithValues = distributeAllSnapshot[productId] || [];
                              const productTotal = quotasWithValues.reduce((sum, q) => sum + q.quota, 0);
                              grandTotal += productTotal;

                              // Buscar nome do produto dos dados simplificados ou cache
                              const productData = simplifiedData.find(
                                (p) => p.contest_product_classification.id === productId
                              );
                              const productName = productData?.contest_product_classification.product_name || `Produto ${productId}`;

                              return (
                                <div key={productId} className="border-b border-gray-200 dark:border-gray-700 pb-3 last:border-0">
                                  <div className="flex justify-between items-center mb-2">
                                    <span className="font-medium text-sm">
                                      {productName}
                                    </span>
                                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                                      {productTotal} cotas
                                    </span>
                                  </div>
                                  <div className="pl-3 space-y-1">
                                    {quotasWithValues.map((quota) => {
                                      const seller = sellers.find((s) => s.id === quota.sellerId);
                                      return (
                                        <div key={quota.sellerId} className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                                          <span>→ {seller?.user?.name || `Vendedor ${quota.sellerId}`}</span>
                                          <span>{quota.quota} cotas</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="border-t border-gray-300 dark:border-gray-600 mt-3 pt-3">
                            <div className="flex justify-between font-semibold">
                              <span>Total Geral:</span>
                              <span className="text-green-600 dark:text-green-400">{grandTotal} cotas</span>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Esta ação irá processar todos os produtos acima. Produtos sem cotas ou que excedam o limite serão ignorados.
                        </p>
                      </>
                    ) : (
                      <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900 rounded">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                          Nenhum produto tem cotas definidas para distribuição.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </DialogDescription>
          </DialogHeader>
          <FileUpload
            files={distributeAllFiles}
            onFilesChange={setDistributeAllFiles}
            inputId="file-upload-distribute-all-seller-seller"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDistributeAllDialog(false);
                setDistributeAllFiles([]);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDistributeAll}
              className={cn(colors.bg_primary)}
              disabled={loadingButton || distributeAllFiles.length === 0}
            >
              {loadingButton ? (
                <>
                  <LoaderSpin />
                  Distribuindo...
                </>
              ) : (
                "Confirmar Distribuição Geral"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de detalhes da distribuição */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-4xl z-[60]" style={{ zIndex: 60 }}>
          <DialogHeader>
            <DialogTitle>
              Detalhes da Distribuição - {selectedProductDetails?.name}
            </DialogTitle>
            <DialogDescription>
              Histórico completo das distribuições para este produto
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {productDetails.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendedor Origem</TableHead>
                    <TableHead>Vendedor Destino</TableHead>
                    <TableHead>Quantidade</TableHead>
                    <TableHead>Data de criação</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado por</TableHead>
                    <TableHead>Data de Atualização</TableHead>
                    <TableHead className="text-center">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productDetails.map((detail: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>
                        {detail.from_seller?.name ||
                          `Vendedor ${detail.from_seller_id}`}
                      </TableCell>
                      <TableCell>
                        {detail?.to_seller?.name ||
                          `Vendedor ${detail.to_seller?.id}`}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">
                          {detail.quota_quantity}
                        </span>{" "}
                        cotas
                      </TableCell>
                      <TableCell>
                      {detail.created_at
                          ? new Date(
                              new Date(detail.created_at).toLocaleString("en-US", {
                                timeZone: "America/Fortaleza",
                              })
                            ).toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            })
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            detail.status?.id == 1
                              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                              : detail.status?.id == 2
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                              : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                          }`}
                        >
                          {detail.status?.id == 1
                            ? "Pendente"
                            : detail.status?.id == 2
                            ? "Aceito"
                            : "Rejeitado"}
                        </span>
                      </TableCell>
                      <TableCell>{detail.created_by?.name || "N/A"}</TableCell>
                      <TableCell>
                        {detail.processed_at
                          ? new Date(
                              new Date(detail.processed_at).toLocaleString("en-US", {
                                timeZone: "America/Fortaleza",
                              })
                            ).toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            })
                          : "N/A"}
                      </TableCell>
                      <TableCell className="text-center">
                        {detail.files && detail.files.length > 0 ? (
                          detail.files.length === 1 ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => window.open(detail.files[0].file_url, "_blank")}
                              title={detail.files[0].file_name || "Baixar arquivo"}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                  title={`${detail.files.length} arquivos`}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {detail.files.map((file: any) => (
                                  <DropdownMenuItem
                                    key={file.id}
                                    onClick={() => window.open(file.file_url, "_blank")}
                                    className="cursor-pointer"
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    {file.file_name || `Arquivo ${file.id}`}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Nenhuma distribuição encontrada para este produto
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDetailsModal(false)}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
